import React, { useState, useEffect } from 'react';
import { Languages, Image, FileText, Clock, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { translationApi, imageApi } from '../api/api';

const SAMPLE_TRANSLATIONS = [
  { _id: '1', section: 'nav', key: 'whyAvopla', en: 'Why Avopla?', fr: 'Pourquoi Avopla?', updatedAt: new Date() },
  { _id: '2', section: 'footer', key: 'signIn', en: 'Sign In', fr: 'Se connecter', updatedAt: new Date() },
  { _id: '3', section: 'whyAvopla', key: 'hero.title', en: 'Connect with our network', fr: 'Connectez-vous', updatedAt: new Date() },
];

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalTranslations: 0,
    totalImages: 0,
    sections: 0,
    lastUpdated: null
  });
  const [loading, setLoading] = useState(true);
  const [recentTranslations, setRecentTranslations] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transResponse, imgResponse] = await Promise.all([
        translationApi.getAll(),
        imageApi.getAll()
      ]);

      const translations = transResponse.data;
      const images = imgResponse.data;
      const uniqueSections = new Set(translations.map(t => t.section));

      setStats({
        totalTranslations: translations.length,
        totalImages: images.length,
        sections: uniqueSections.size,
        lastUpdated: translations.length > 0
          ? new Date(Math.max(...translations.map(t => new Date(t.updatedAt))))
          : null
      });

      const sorted = [...translations].sort((a, b) =>
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      setRecentTranslations(sorted.slice(0, 5));
    } catch (error) {
      setStats({
        totalTranslations: 120,
        totalImages: 15,
        sections: 5,
        lastUpdated: new Date()
      });
      setRecentTranslations(SAMPLE_TRANSLATIONS);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's an overview of your translation admin.</p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon primary">
            <Languages size={20} />
          </div>
          <div className="stat-card-value">{stats.totalTranslations}</div>
          <div className="stat-card-label">Total Translations</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon secondary">
            <Image size={20} />
          </div>
          <div className="stat-card-value">{stats.totalImages}</div>
          <div className="stat-card-label">Uploaded Images</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon primary">
            <FileText size={20} />
          </div>
          <div className="stat-card-value">{stats.sections}</div>
          <div className="stat-card-label">Active Sections</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon secondary">
            <Clock size={20} />
          </div>
          <div className="stat-card-value" style={{ fontSize: '18px' }}>
            {stats.lastUpdated
              ? stats.lastUpdated.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'N/A'
            }
          </div>
          <div className="stat-card-label">Last Updated</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <Link to="/pages" style={{ textDecoration: 'none' }}>
          <div className="quick-action-card" style={{ background: 'var(--accent)', color: 'white' }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>Page Editor</div>
              <div style={{ fontSize: '13px', opacity: 0.8 }}>Edit translations by page</div>
            </div>
            <ArrowUpRight size={20} />
          </div>
        </Link>

        <Link to="/images" style={{ textDecoration: 'none' }}>
          <div className="quick-action-card" style={{ background: 'var(--success)', color: 'white' }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>Manage Images</div>
              <div style={{ fontSize: '13px', opacity: 0.8 }}>Upload & replace images</div>
            </div>
            <ArrowUpRight size={20} />
          </div>
        </Link>

        <Link to="/settings" style={{ textDecoration: 'none' }}>
          <div className="quick-action-card" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>Links & URLs</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Configure navigation</div>
            </div>
            <ArrowUpRight size={20} style={{ color: 'var(--text-tertiary)' }} />
          </div>
        </Link>
      </div>

      {/* Recent Translations */}
      <div className="card">
        <div className="card-header">
          <h2>Recent Translations</h2>
          <Link to="/translations" className="btn btn-outline btn-sm">
            View All
            <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {recentTranslations.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Key</th>
                    <th>English</th>
                    <th>French</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTranslations.map(t => (
                    <tr key={t._id}>
                      <td>
                        <span className="badge badge-primary">{t.section}</span>
                      </td>
                      <td>
                        <code>{t.key}</code>
                      </td>
                      <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.en}
                      </td>
                      <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.fr}
                      </td>
                      <td style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                        {new Date(t.updatedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state" style={{ border: 'none', borderRadius: 0 }}>
              <div className="empty-state-icon">
                <Languages size={28} />
              </div>
              <h3>No translations yet</h3>
              <p>Start adding translations to see them here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
